/**
 * React hook для управления упражнениями
 */

import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { Exercise, ExerciseCreate, ExerciseUpdate, ExerciseFilters, MuscleGroup } from '../types';

export const useExercises = (filters: ExerciseFilters = {}) => {
  const queryClient = useQueryClient();

  // Запрос списка упражнений
  const {
    data: exercises = [],
    isLoading,
    error,
    refetch
  } = useQuery(
    ['exercises', filters],
    () => apiService.getExercises(filters),
    {
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при загрузке упражнений';
        toast.error(errorMessage);
      }
    }
  );

  // Запрос общего количества упражнений (без пагинации)
  const countFilters = { ...filters };
  delete countFilters.skip;
  delete countFilters.limit;
  
  const {
    data: countData = { total: 0 },
    isLoading: isCountLoading,
    error: countError
  } = useQuery(
    ['exercises-count', countFilters],
    () => apiService.getExercisesCount(countFilters),
    {
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при загрузке количества упражнений';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для создания упражнения
  const createExerciseMutation = useMutation(
    (data: ExerciseCreate) => apiService.createExercise(data),
    {
      onSuccess: () => {
        toast.success('Упражнение создано успешно!');
        queryClient.invalidateQueries('exercises');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при создании упражнения';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для обновления упражнения
  const updateExerciseMutation = useMutation(
    ({ id, data }: { id: number; data: ExerciseUpdate }) => 
      apiService.updateExercise(id, data),
    {
      onSuccess: () => {
        toast.success('Упражнение обновлено успешно!');
        queryClient.invalidateQueries('exercises');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при обновлении упражнения';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для удаления упражнения
  const deleteExerciseMutation = useMutation(
    (id: number) => apiService.deleteExercise(id),
    {
      onSuccess: () => {
        toast.success('Упражнение удалено успешно!');
        queryClient.invalidateQueries('exercises');
      },
      onError: (error: any) => {
        // Проверяем, связана ли ошибка с использованием упражнения в тренировках
        if (error?.response?.status === 400 && error?.response?.data?.detail?.includes('used in workouts')) {
          toast.error('Нельзя удалить упражнение, которое используется в тренировках. Сначала удалите его из тренировок.');
        } else {
          const errorMessage = error.detail || 'Ошибка при удалении упражнения';
          toast.error(errorMessage);
        }
      }
    }
  );

  return {
    // Данные
    exercises,
    totalCount: countData.total,
    isLoading: isLoading || isCountLoading,
    error: error || countError,
    
    // Действия
    createExercise: createExerciseMutation.mutate,
    updateExercise: updateExerciseMutation.mutate,
    deleteExercise: deleteExerciseMutation.mutate,
    refetch,
    
    // Состояния мутаций
    isCreateLoading: createExerciseMutation.isLoading,
    isUpdateLoading: updateExerciseMutation.isLoading,
    isDeleteLoading: deleteExerciseMutation.isLoading,
  };
};

// Хук для работы с одним упражнением
export const useExercise = (id: number) => {
  const queryClient = useQueryClient();

  const {
    data: exercise,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['exercise', id],
    () => apiService.getExercise(id),
    {
      enabled: !!id,
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при загрузке упражнения';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для обновления упражнения
  const updateExerciseMutation = useMutation(
    (data: ExerciseUpdate) => apiService.updateExercise(id, data),
    {
      onSuccess: () => {
        toast.success('Упражнение обновлено успешно!');
        queryClient.invalidateQueries(['exercise', id]);
        queryClient.invalidateQueries('exercises');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при обновлении упражнения';
        toast.error(errorMessage);
      }
    }
  );

  return {
    // Данные
    exercise,
    isLoading,
    error,
    
    // Действия
    updateExercise: updateExerciseMutation.mutate,
    refetch,
    
    // Состояния мутаций
    isUpdateLoading: updateExerciseMutation.isLoading,
  };
};

// Хук для работы с группами мышц
export const useMuscleGroups = () => {
  const queryClient = useQueryClient();

  // Запрос списка групп мышц
  const {
    data: muscleGroups = [],
    isLoading,
    error,
    refetch
  } = useQuery(
    'muscle-groups',
    () => apiService.getMuscleGroups(),
    {
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при загрузке групп мышц';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для создания группы мышц
  const createMuscleGroupMutation = useMutation(
    (data: { name: string; description?: string }) => 
      apiService.createMuscleGroup(data),
    {
      onSuccess: () => {
        toast.success('Группа мышц создана успешно!');
        queryClient.invalidateQueries('muscle-groups');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при создании группы мышц';
        toast.error(errorMessage);
      }
    }
  );

  return {
    // Данные
    muscleGroups,
    isLoading,
    error,
    
    // Действия
    createMuscleGroup: createMuscleGroupMutation.mutate,
    refetch,
    
    // Состояния мутаций
    isCreateLoading: createMuscleGroupMutation.isLoading,
  };
}; 